import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Tabs } from 'antd';

import { displayNewsHeadline, fetchPassedNews, selectors } from 'state';
import { LoadingScreen } from 'components';

import './NewsHistory2.css';
import { TraineeSessionContext } from './TraineeSessionContext';

const { TabPane } = Tabs;

type NewsRow = {
  id: string | number;
  headline: string;
  rephrased_headline?: string | null;
  geography?: string | null;
  sectors?: string[] | null;
};

const normalizeGeo = (g?: string | null) =>
  g && g.trim() ? g.trim() : 'Unknown';

const normalizeSector = (s?: string | null) =>
  s && s.trim() ? s.trim() : 'Unassigned';

const zebraRowClass = (_: any, index: number) =>
  index % 2 === 0 ? 'news-row-light' : 'news-row-dark';

const NewsHistory2: React.FC = () => {
  const dispatch = useDispatch();
  const traineeContext = useContext(TraineeSessionContext);

  const newsList = useSelector(selectors.newsList) as NewsRow[];
  const lastNews = useSelector(selectors.lastNews) as unknown as
    | Record<string, true>
    | undefined;
  const { news_fetched, fetching_news, id, news_grouping_mode } = useSelector(
    selectors.activeSession,
  );

  useEffect(() => {
    if (news_fetched) return;
    if (!id) return;
    dispatch(fetchPassedNews({ session_id: id }));
  }, [dispatch, news_fetched, id]);

  const showGroupedTables = news_grouping_mode !== 'all';

  const { groupLabels, groupedNews } = useMemo<{
    groupLabels: string[];
    groupedNews: Record<string, NewsRow[]>;
  }>(() => {
    if (!showGroupedTables) {
      return { groupLabels: [], groupedNews: {} };
    }

    const grouped: Record<string, NewsRow[]> = {};
    for (const item of newsList || []) {
      if (news_grouping_mode === 'sector') {
        const sectors = (item.sectors || [])
          .map(normalizeSector)
          .filter(Boolean);
        const bucketKeys = sectors.length ? sectors : ['Unassigned'];
        bucketKeys.forEach(bucketKey => {
          if (!grouped[bucketKey]) grouped[bucketKey] = [];
          grouped[bucketKey].push(item);
        });
        continue;
      }

      const geo = normalizeGeo(item.geography);
      if (!grouped[geo]) grouped[geo] = [];
      grouped[geo].push(item);
    }
    const labels = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
    return { groupLabels: labels, groupedNews: grouped };
  }, [newsList, news_grouping_mode, showGroupedTables]);

  useEffect(() => {
    if (!showGroupedTables) return;
    if (!groupLabels.length) return;

    traineeContext.setGeoLeft(prev =>
      prev && groupLabels.includes(prev) ? prev : groupLabels[0],
    );
    traineeContext.setGeoMid(prev =>
      prev && groupLabels.includes(prev)
        ? prev
        : groupLabels[1] || groupLabels[0],
    );
  }, [groupLabels, showGroupedTables, traineeContext]);

  const columns = useMemo(
    () => [
      {
        title: 'Headline',
        dataIndex: 'headline',
        key: 'headline',
        render: (_value: string, record: NewsRow) => (
          <div className="news-cell-wrap">{displayNewsHeadline(record)}</div>
        ),
      },
    ],
    [],
  );

  const [scrollY, setScrollY] = useState<number>(520);

  useEffect(() => {
    const compute = () => {
      // tweak this offset based on your page header/filters etc.
      const offset = 325;
      const y = Math.max(240, window.innerHeight - offset);
      setScrollY(y);
    };

    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  const makeRowClassName =
    (opts?: { highlightLast?: boolean }) =>
    (record: NewsRow, index: number) => {
      const base = zebraRowClass(record, index);

      if (!opts?.highlightLast) return base;

      const key = String(record.id);
      if (Object.prototype.hasOwnProperty.call(lastNews, key)) {
        const latestShade =
          index % 2 === 0 ? 'news-row-last-a' : 'news-row-last-b';
        return `${latestShade} ${base}`;
      }

      return base;
    };
  const rowClassWithLast = useMemo(
    () => makeRowClassName({ highlightLast: true }),
    [lastNews],
  );

  // Keep same behavior as before: wait until lastNews exists
  // (since the “last” mechanic depends on it for table #3)
  if (lastNews === undefined) return <LoadingScreen />;

  return (
    <div className="news-history-grid">
      {showGroupedTables && (
        <div className="news-history-col">
          <Tabs
            activeKey={traineeContext.geoLeft}
            onChange={traineeContext.setGeoLeft}
            size="small"
            animated={false}
            tabBarGutter={8}
          >
            {groupLabels.map(groupLabel => (
              <TabPane tab={groupLabel} key={groupLabel} />
            ))}
          </Tabs>

          <Table
            dataSource={groupedNews[traineeContext.geoLeft] || []}
            bordered
            loading={fetching_news}
            rowKey="id"
            tableLayout="fixed"
            columns={columns}
            rowClassName={rowClassWithLast}
            scroll={{ y: scrollY }}
            pagination={false}
            style={{ border: '1px solid #efefef' }}
          />
        </div>
      )}

      {showGroupedTables && (
        <div className="news-history-col">
          <Tabs
            activeKey={traineeContext.geoMid}
            onChange={traineeContext.setGeoMid}
            size="small"
            animated={false}
            tabBarGutter={8}
          >
            {groupLabels.map(groupLabel => (
              <TabPane tab={groupLabel} key={groupLabel} />
            ))}
          </Tabs>

          <Table
            dataSource={groupedNews[traineeContext.geoMid] || []}
            bordered
            loading={fetching_news}
            rowKey="id"
            tableLayout="fixed"
            columns={columns}
            rowClassName={rowClassWithLast}
            scroll={{ y: scrollY }}
            pagination={false}
            style={{ border: '1px solid #efefef' }}
          />
        </div>
      )}

      {/* All news table */}
      <div className="news-history-col">
        <div className="news-history-header" style={{ marginTop: 4 }}>
          All
        </div>

        <Table
          dataSource={newsList}
          bordered
          loading={fetching_news}
          rowKey="id"
          tableLayout="fixed"
          columns={columns}
          rowClassName={rowClassWithLast}
          // rowClassName={allNewsRowClassName}
          scroll={{ y: scrollY }}
          pagination={false}
          style={{ marginTop: 19, border: '1px solid #efefef' }}
          // pagination={{
          //   pageSize,
          //   total: newsList.length,
          //   showSizeChanger: false,
          // }}
        />
      </div>
    </div>
  );
};

export default NewsHistory2;
